import classNames from 'classnames'
import {
    FiArrowDownRight,
    FiArrowUpLeft,
    FiCreditCard,
    FiDollarSign,
    FiLink2,
    FiRepeat,
} from 'react-icons/fi'
import { IoBusinessOutline, IoWalletOutline } from 'react-icons/io5'
import {
    IMyTransactionHint,
    signOf,
    THintIcon,
    TMyTransactionKind,
} from './helpers'

// The marks a row and its details sheet share, drawn the way the fund tab's
// ledger draws them so a payment looks like the same thing in both places:
// what came in is green and points down-right, what went out is red and points
// up-left, in the tile and on the amount alike.

const TILES: Record<
    TMyTransactionKind,
    { Icon: typeof FiArrowDownRight; className: string; label: string }
> = {
    in: {
        Icon: FiArrowDownRight,
        className: 'bg-success-2 text-success-on',
        label: 'دریافتی',
    },
    out: {
        Icon: FiArrowUpLeft,
        className: 'bg-danger-soft text-danger-on',
        label: 'پرداختی',
    },
    wallet_in: {
        Icon: IoWalletOutline,
        className: 'bg-success-2 text-success-on',
        label: 'شارژ کیف پول',
    },
    wallet_out: {
        Icon: IoWalletOutline,
        className: 'bg-danger-soft text-danger-on',
        label: 'برداشت از کیف پول',
    },
    move: {
        Icon: FiRepeat,
        className: 'bg-divider text-text-secondary',
        label: 'جابه‌جایی',
    },
}

// A wallet row keeps the purse — it is the one thing that tells it apart at a
// glance — so the direction is said with a small arrow pinned to the corner,
// the same arrow and the same green or red the other rows use.
const BADGES: Partial<
    Record<
        TMyTransactionKind,
        { Icon: typeof FiArrowDownRight; className: string }
    >
> = {
    wallet_in: {
        Icon: FiArrowDownRight,
        className: 'bg-success-on text-white',
    },
    wallet_out: {
        Icon: FiArrowUpLeft,
        className: 'bg-danger-on text-white',
    },
}

/** Which way the money went — by shape as well as colour. */
export function KindTile({
    kind,
    large = false,
}: {
    kind: TMyTransactionKind
    large?: boolean
}) {
    const { Icon, className } = TILES[kind]
    const badge = BADGES[kind]
    return (
        <span
            aria-hidden
            className={classNames(
                'relative flex shrink-0 items-center justify-center',
                large ? 'h-12 w-12 rounded-[14px]' : 'h-9 w-9 rounded-[10px]',
                className
            )}
        >
            <Icon className={large ? 'h-6 w-6' : 'h-[18px] w-[18px]'} />
            {badge && (
                <span
                    className={classNames(
                        'absolute flex items-center justify-center rounded-full ring-2 ring-white',
                        large
                            ? '-bottom-1 -left-1 h-5 w-5'
                            : '-bottom-1 -left-1 h-[15px] w-[15px]',
                        badge.className
                    )}
                >
                    <badge.Icon
                        className={large ? 'h-3 w-3' : 'h-2.5 w-2.5'}
                        strokeWidth={3}
                    />
                </span>
            )}
        </span>
    )
}

export const kindLabel = (kind: TMyTransactionKind) => TILES[kind].label

// Green for what reached them, red for what left them — the fund tab's two
// colours, on the tile and on the figure at once, so a row never says «in» in
// one place and «out» in another. A move carries no sign and no colour: it
// never touched their pocket.
const AMOUNT_COLOURS: Record<TMyTransactionKind, string> = {
    in: 'text-success-on',
    out: 'text-danger-on',
    wallet_in: 'text-success-on',
    wallet_out: 'text-danger-on',
    move: 'text-text-body',
}

export function KindAmount({
    amount,
    kind,
    large = false,
}: {
    amount: number | null | undefined
    kind: TMyTransactionKind
    large?: boolean
}) {
    return (
        <span
            className={classNames(
                'whitespace-nowrap font-bold',
                large ? 'text-2xl leading-9' : 'text-[15px] leading-6',
                AMOUNT_COLOURS[kind]
            )}
        >
            {/* The sign is isolated with the digits: `+` and `−` are
                bidi-neutral, and loose in an RTL run they are painted at the
                far end of «… تومان», away from the number. */}
            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                {signOf(kind)}
                {(amount ?? 0).toLocaleString()}
            </span>{' '}
            <span
                className={classNames(
                    'font-normal',
                    large ? 'text-sm' : 'text-[11px]'
                )}
            >
                تومان
            </span>
        </span>
    )
}

/**
 * «کارمزد −۱۰,۰۰۰» under a payment's amount: what left the member with it, so
 * a «−۵۰,۰۰۰» beside the «+۶۰,۰۰۰» that paid for it adds up in the list too.
 */
export function CommissionLine({ commission }: { commission: number }) {
    return (
        <span className="whitespace-nowrap text-[11px] leading-4 text-text-secondary">
            کارمزد{' '}
            <span
                dir="ltr"
                className="font-bold text-danger-on"
                style={{ unicodeBidi: 'isolate' }}
            >
                −{commission.toLocaleString()}
            </span>
        </span>
    )
}

const HINT_ICONS: Record<THintIcon, typeof FiArrowDownRight> = {
    wallet: IoWalletOutline,
    gateway: FiCreditCard,
    cash: FiDollarSign,
    bank: IoBusinessOutline,
    link: FiLink2,
}

/**
 * Where the money came out of, said in the one line a member actually looks
 * for: «برداشت از کیف پول», «با درگاه بانکی», «برای پرداخت به صندوق مهر».
 */
export function HintChip({
    hint,
    className,
}: {
    hint: IMyTransactionHint
    className?: string
}) {
    const Icon = HINT_ICONS[hint.icon]
    return (
        <span
            className={classNames(
                'inline-flex max-w-full items-center gap-1 rounded-full bg-sunken px-1.5 py-px text-[11px] leading-5 text-text-secondary',
                className
            )}
        >
            <Icon aria-hidden className="h-3 w-3 shrink-0 text-text-muted" />
            <span className="truncate">{hint.label}</span>
        </span>
    )
}

/** «۱۲٬۳۰۰٬۰۰۰ تومان» for a table cell. */
export function Toman({ amount }: { amount: number }) {
    return (
        <>
            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                {amount.toLocaleString()}
            </span>{' '}
            <span className="text-[11px] font-normal">تومان</span>
        </>
    )
}
